import React, { useState } from 'react';
import {
  Box,
  Center,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { DialogProps } from 'clipsal-cortex-types/src/common/chakra-extension-types';
import { TimePicker } from 'clipsal-cortex-ui/src/components/time-picker/TimePicker';

type Props = { onSave: (time: string) => void; selectedTime?: string } & DialogProps;

type TimeSelectState = {
  hours: number;
  minutes: number;
  amOrPm: 'AM' | 'PM';
};

export function TimeSelectDrawer({ isOpen, onClose, onSave, selectedTime }: Props) {
  const initialState = getInitialState();
  const [state, setState] = useState<TimeSelectState>(initialState);
  const { hours, minutes, amOrPm } = state;
  const timePickerGradientColor = useColorModeValue('253, 253, 253', '41, 49, 51');
  const timePickerBorderColor = useColorModeValue('#293133', '#fff');
  const bgColor = useColorModeValue('#fff', '#293133');
  const greyTextColor = useColorModeValue('customGrey.700', 'customGrey.400');
  const buttonTextColor = useColorModeValue('primaryBranding.600', 'primaryBranding.200');
  const { t } = useTranslation();

  function getInitialState() {
    if (!selectedTime) {
      return {
        hours: 12,
        minutes: 0,
        amOrPm: 'AM',
      } as TimeSelectState;
    }

    // Convert time back to 12 hour format.
    const [hoursFromSchedule, minutesFromSchedule] = selectedTime.split(':');
    let hoursDefaultValue = Number(hoursFromSchedule) > 12 ? Number(hoursFromSchedule) - 12 : Number(hoursFromSchedule);
    if (hoursDefaultValue === 0) hoursDefaultValue = 12;
    return {
      hours: hoursDefaultValue,
      minutes: Number(minutesFromSchedule),
      amOrPm: Number(hoursFromSchedule) >= 12 ? 'PM' : 'AM',
    } as TimeSelectState;
  }

  function handleSave() {
    // Reformat the time from UI to match the API (ie. 7:00 -> 07:00:00) to facilitate validation checking.
    const minutesPadded = minutes < 10 ? `0${minutes}` : minutes;
    const hours24hTime: number | string = amOrPm === 'PM' && hours !== 12 ? hours + 12 : hours;

    let hoursPadded = hours24hTime < 10 ? `0${hours24hTime}` : hours24hTime;
    if (hours === 12 && amOrPm === 'AM') {
      hoursPadded = '00';
    }

    onSave(`${hoursPadded}:${minutesPadded}:00`);
    onClose();
  }

  return (
    <Drawer placement="bottom" onClose={onClose} isOpen={isOpen}>
      <DrawerOverlay />
      <DrawerContent data-testid="time-select-drawer" bg={'transparent'}>
        <DrawerBody px={2}>
          <Box borderRadius={'15px'} bg={bgColor}>
            <Center borderBottom={'1px solid'} borderColor={'customGrey.500'} py={3}>
              <Text textAlign={'center'} color={greyTextColor} fontSize={'sm'}>
                {t('Devices.time select')}
              </Text>
            </Center>

            <Center as={'button'} w={'100%'} borderBottom={'1px solid'} borderColor={'customGrey.500'} py={3}>
              <TimePicker
                sliderColumnWidth={'75px'}
                defaultHours={initialState.hours}
                defaultMinutes={initialState.minutes}
                defaultAmOrPm={amOrPm}
                onChange={(value) => {
                  setState((prevState) => {
                    return { ...prevState, ...value };
                  });
                }}
                gradientColor={timePickerGradientColor}
                borderColor={timePickerBorderColor}
              />
            </Center>

            <Center
              data-testid="save-time-select-drawer"
              as={'button'}
              w={'100%'}
              py={3}
              borderRadius={'15px'}
              bg={bgColor}
              onClick={handleSave}
            >
              <Text fontWeight={600} color={buttonTextColor}>
                {t('Common.confirm')}
              </Text>
            </Center>
          </Box>

          <Center
            data-testid="close-time-select-drawer"
            as={'button'}
            w={'100%'}
            onClick={onClose}
            py={3}
            mt={2}
            borderRadius={'15px'}
            bg={bgColor}
          >
            <Text color={greyTextColor}>{t('Common.cancel')}</Text>
          </Center>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
